import { useState, useEffect, useRef } from 'react';
import Box from '@liquidweb-dev/undercurrent/Box';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Slide from '@liquidweb-dev/undercurrent/Slide';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import Button from '@liquidweb-dev/undercurrent/Button';
import CancellationModalHeader from './CancellationModalHeader';

export interface RequestAssistanceProps {
	handleRequestAssistanceSubmit: () => void;
	onCancelWithoutAssistance: () => void;
}

const RequestAssistance = ({
	handleRequestAssistanceSubmit,
	onCancelWithoutAssistance,
}: RequestAssistanceProps) => {
	const [animateContent, setAnimateContent] = useState(false);
	const animationContainerRef = useRef();

	useEffect(() => {
		const timeout = setTimeout(() => {
			setAnimateContent(true);
		}, 300);

		return () => clearTimeout(timeout);
	}, []);

	return (
		<Box ref={animationContainerRef}>
			<Slide
				in={animateContent}
				container={animationContainerRef.current}
				direction="up"
				timeout={700}
			>
				<Stack
					sx={{
						flexDirection: 'column',
						alignItems: 'center',
						gap: 200,
						alignSelf: 'stretch',
					}}
				>
					<CancellationModalHeader
						isAnimationRequired
						animateContent={animateContent}
					>
						<Stack
							sx={{
								display: 'flex',
								flexDirection: 'row',
								width: '100%',
								justifyContent: 'center',
							}}
						>
							<Typography
								variant="title-sm"
								sx={[
									{
										fontWeight: 500,
									},
									(theme) => ({ color: theme.vars.palette.uc.text.main }),
								]}
							>
								Need to cancel?
							</Typography>
							<Typography
								variant="title-sm"
								sx={[
									{
										fontWeight: 500,
									},
									(theme) => ({
										color: theme.vars.palette.uc.highlight.text.main,
									}),
								]}
							>
								&nbsp;We&apos;re here to help.
							</Typography>
						</Stack>
						<Typography
							variant="body-sm"
							sx={{
								textAlign: 'center',
							}}
						>
							We&apos;ll handle everything for you—securely and hassle-free.
							Simply submit your request, and a member of our team will contact
							you within 24-48 hours to help you begin the cancellation process
							smoothly and safely.
						</Typography>
					</CancellationModalHeader>
					<Stack
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							alignSelf: 'stretch',
							gap: 100,
						}}
					>
						<Button
							color="highlight"
							size="large"
							variant="primary"
							sx={{
								paddingY: 100,
								paddingX: 200,
							}}
							onClick={handleRequestAssistanceSubmit}
							fullWidth
						>
							Request Assistance
						</Button>
						<Button
							size="large"
							variant="secondary"
							sx={{
								paddingY: 100,
								paddingX: 200,
							}}
							onClick={onCancelWithoutAssistance}
							fullWidth
						>
							Cancel without assistance
						</Button>
					</Stack>
				</Stack>
			</Slide>
		</Box>
	);
};

export default RequestAssistance;
